/**
 * Stub catalog — single source of truth for all Phase-2 stub atoms.
 * Consumed by both web and mobile to render PhaseTwoStub cards.
 * Each entry maps 1:1 to an R-ID from the v1.1 requirements anchor.
 */

export type StubStatus =
  | { kind: "live" }
  // whenLabel is optional: omit it for roadmap items with no committed date
  // (rendered as an undated "in the delivery roadmap" pill). Populate it only
  // with a genuinely-future, non-self-falsifying date.
  | { kind: "next"; whenLabel?: string }
  | { kind: "future"; whenLabel?: string };

export interface StubAtom {
  slug: string;
  rId: string;
  name: string;
  description: string;
  status: StubStatus;
  sequencingReason: string;
  liveLink?: { url: string; label: string; labelKey?: string };
  // i18n keys (D1/W5) — ADDITIVE siblings to the English fields above, which
  // remain the SSoT (mobile reads them verbatim, Phase 3). A trilingual web
  // consumer resolves these via `t()`; when a key is absent the English field is
  // the fallback. Populated per route-group workstream: SERVICES_STUBS +
  // REPORT_STUBS in W5a; MENU_STUBS in W5b; SUPPORT_STUBS in W5c.
  nameKey?: string;
  descriptionKey?: string;
  sequencingReasonKey?: string;
}

// ---------------------------------------------------------------------------
// Services tab — 9 SR catalog cards
// ---------------------------------------------------------------------------

export const SERVICES_STUBS: StubAtom[] = [
  {
    slug: "start-stop-transfer",
    rId: "R7.2",
    name: "Start, Stop, or Transfer Service",
    description:
      "Move into a property, close an account, or transfer service to a new address — all from your phone, no office visit needed.",
    status: { kind: "live" },
    sequencingReason:
      "Moving home is the request we hear most, so it was the first thing we built.",
    liveLink: {
      url: "https://pelicanenergytci.com/apply-for-new-service",
      label: "Current form on pelicanenergytci.com",
      labelKey: "stubs.startStopTransfer.link",
    },
    nameKey: "stubs.startStopTransfer.name",
    descriptionKey: "stubs.startStopTransfer.description",
    sequencingReasonKey: "stubs.startStopTransfer.why",
  },
  {
    slug: "dusk-to-dawn",
    rId: "R7.3",
    name: "Apply for Dusk-to-Dawn Light",
    description:
      "Request installation or replacement of a residential street-side light, including quantity needed and location.",
    status: { kind: "future" },
    sequencingReason:
      "We're finishing the most-used requests first. This one needs a form that captures how many lights you need and exactly where.",
    nameKey: "stubs.duskToDawn.name",
    descriptionKey: "stubs.duskToDawn.description",
    sequencingReasonKey: "stubs.duskToDawn.why",
  },
  {
    slug: "apply-new-service",
    rId: "R7.4",
    name: "Apply for New Service",
    description:
      "Request a new electricity service connection at a property — initial application, supporting documents, schedule connection date.",
    status: { kind: "future" },
    sequencingReason:
      "Connecting a brand-new property needs a site visit booked and tracked — we're building that scheduling step next.",
    liveLink: {
      url: "https://pelicanenergytci.com/apply-for-new-service",
      label: "Current form on pelicanenergytci.com",
      labelKey: "stubs.applyNewService.link",
    },
    nameKey: "stubs.applyNewService.name",
    descriptionKey: "stubs.applyNewService.description",
    sequencingReasonKey: "stubs.applyNewService.why",
  },
  {
    slug: "account-closure",
    rId: "R7.5",
    name: "Close My Account",
    description:
      "Permanently close your service account when moving out, with final-bill summary and refund-of-deposit workflow.",
    status: { kind: "future" },
    sequencingReason:
      "Closing an account runs the same final-reading and settlement checks as a transfer, so it follows once that flow is settled.",
    nameKey: "stubs.accountClosure.name",
    descriptionKey: "stubs.accountClosure.description",
    sequencingReasonKey: "stubs.accountClosure.why",
  },
  {
    slug: "owners-consent",
    rId: "R7.6",
    name: "Submit Owner’s Consent",
    description:
      "Authorize a service change on a property you own when the occupant is making the request — with signature capture.",
    status: { kind: "future" },
    sequencingReason:
      "This one needs secure signature capture in the app before we can accept it.",
    nameKey: "stubs.ownersConsent.name",
    descriptionKey: "stubs.ownersConsent.description",
    sequencingReasonKey: "stubs.ownersConsent.why",
  },
  {
    slug: "personal-guarantee",
    rId: "R7.7",
    name: "Submit Personal Guarantee",
    description:
      "Provide a personal guarantee for a service connection or upgrade where Pelican requires one.",
    status: { kind: "future" },
    sequencingReason:
      "It needs the same secure signature step as Owner's Consent, so the two arrive together.",
    nameKey: "stubs.personalGuarantee.name",
    descriptionKey: "stubs.personalGuarantee.description",
    sequencingReasonKey: "stubs.personalGuarantee.why",
  },
  {
    slug: "power-line-clearance",
    rId: "R7.8",
    name: "Power Line Clearance Forms",
    description:
      "SafeMoves (moving heavy equipment), High-Load (oversized vehicle), Line Locate (digging) — request safety clearance from Pelican Energy TCI.",
    status: { kind: "future" },
    sequencingReason:
      "Clearance work involves your contractor as well as you — we're getting the requests you make on your own right first.",
    liveLink: {
      url: "https://pelicanenergytci.com/safemoves",
      label: "SafeMoves on pelicanenergytci.com",
      labelKey: "stubs.powerLineClearance.link",
    },
    nameKey: "stubs.powerLineClearance.name",
    descriptionKey: "stubs.powerLineClearance.description",
    sequencingReasonKey: "stubs.powerLineClearance.why",
  },
  {
    slug: "damage-claims",
    rId: "R6.14",
    name: "Damage Claims",
    description:
      "Submit a claim for damage caused by an outage — within 10 days, with repair quotes attached. Decision in 15 business days.",
    status: { kind: "live" },
    sequencingReason:
      "The claims process already existed — we brought it into the app so you don't have to go looking for it.",
    liveLink: {
      url: "https://pelicanenergytci.com/power-outages-damage-claims/",
      label: "Current process on pelicanenergytci.com",
      labelKey: "stubs.damageClaims.link",
    },
    nameKey: "stubs.damageClaims.name",
    descriptionKey: "stubs.damageClaims.description",
    sequencingReasonKey: "stubs.damageClaims.why",
  },
  {
    slug: "upgrade-extend",
    rId: "R7.13+R7.14",
    name: "Upgrade Service or Grid Extension",
    description:
      "Request a capacity upgrade (e.g. single-phase to 3-phase, increased amperage) or grid extension to a remote property.",
    status: { kind: "future" },
    sequencingReason:
      "An upgrade needs an engineering survey of your supply before anyone can quote it. We're working out how to book that from the app.",
    nameKey: "stubs.upgradeExtend.name",
    descriptionKey: "stubs.upgradeExtend.description",
    sequencingReasonKey: "stubs.upgradeExtend.why",
  },
];

// ---------------------------------------------------------------------------
// Report tab — 2 stub entries (existing Outage is worked, not stubbed)
// ---------------------------------------------------------------------------

export const REPORT_STUBS: StubAtom[] = [
  {
    slug: "hazard",
    rId: "R6.9",
    name: "Report a Hazard",
    description:
      "Downed power line, exposed wires, hazardous tree-on-line, or other safety concern. Photo upload supported.",
    status: { kind: "future" },
    sequencingReason:
      "We're building the photo upload and the direct line to field crews that a safety report depends on.",
    nameKey: "stubs.hazard.name",
    descriptionKey: "stubs.hazard.description",
    sequencingReasonKey: "stubs.hazard.why",
  },
  {
    slug: "streetlight",
    rId: "R6.10",
    name: "Report a Faulty Streetlight",
    description:
      "Tap the streetlight on the map OR enter the pole number stamped on the post.",
    status: { kind: "live" },
    sequencingReason:
      "Reporting a dark street should take seconds, so we started with the map and the pole-number lookup.",
    nameKey: "stubs.streetlight.name",
    descriptionKey: "stubs.streetlight.description",
    sequencingReasonKey: "stubs.streetlight.why",
  },
];

// ---------------------------------------------------------------------------
// Customer Support tab — 2 stubs (AI Assistant is worked, linked not stubbed)
// ---------------------------------------------------------------------------

export const SUPPORT_STUBS: StubAtom[] = [
  {
    slug: "live-chat",
    rId: "R9.4",
    name: "Live Chat with Pelican Energy TCI Support",
    description:
      "Real-time chat with a Pelican Energy TCI customer-service agent during business hours.",
    status: { kind: "future" },
    sequencingReason:
      "Live chat only helps if someone is always there to answer, so we're getting the support desk ready first.",
    nameKey: "stubs.liveChat.name",
    descriptionKey: "stubs.liveChat.description",
    sequencingReasonKey: "stubs.liveChat.why",
  },
  {
    slug: "voice-alexa",
    rId: "R9.7",
    name: "Amazon Alexa Voice Integration",
    description:
      "Ask Alexa: ‘What’s my Pelican balance?’ / ‘Report a power outage.’",
    status: { kind: "future" },
    sequencingReason:
      "We're asking customers whether voice is something they'd actually use before we build it.",
    nameKey: "stubs.voiceAlexa.name",
    descriptionKey: "stubs.voiceAlexa.description",
    sequencingReasonKey: "stubs.voiceAlexa.why",
  },
];

// ---------------------------------------------------------------------------
// Menu tab — 5 settings/account items
// ---------------------------------------------------------------------------

export const MENU_STUBS: StubAtom[] = [
  {
    slug: "notifications",
    rId: "R8.5",
    name: "Notification Channels & Quiet Hours",
    description:
      "Choose delivery channels (Email, SMS, Push) and set quiet hours for each notification type.",
    status: { kind: "future" },
    sequencingReason:
      "Sending texts and emails reliably — and remembering exactly which ones you want — is the piece we're building now.",
    nameKey: "stubs.notifications.name",
    descriptionKey: "stubs.notifications.description",
    sequencingReasonKey: "stubs.notifications.why",
  },
  {
    slug: "autopay",
    rId: "R4.8",
    name: "Autopay",
    description:
      "Set up automatic payment of your monthly bill from a saved card.",
    status: { kind: "future" },
    sequencingReason:
      "Storing a card safely enough to charge it automatically needs bank-grade protection, which we're putting in place.",
    nameKey: "stubs.autopay.name",
    descriptionKey: "stubs.autopay.description",
    sequencingReasonKey: "stubs.autopay.why",
  },
  {
    slug: "account-update",
    rId: "R2.3",
    name: "Account Update",
    description:
      "Update your contact info, mailing address, and communication preferences.",
    status: { kind: "live" },
    sequencingReason:
      "Keeping your contact details current is basic to everything else, so it shipped in the first release.",
    nameKey: "stubs.accountUpdate.name",
    descriptionKey: "stubs.accountUpdate.description",
    sequencingReasonKey: "stubs.accountUpdate.why",
  },
  {
    slug: "vacation-mode",
    rId: "W3.1",
    name: "Vacation Mode",
    description:
      "Let Pelican know when you're away — we'll watch for unusual usage and alert you.",
    status: { kind: "live" },
    sequencingReason:
      "Plenty of homes here sit empty for part of the year, so this was worth building early.",
    nameKey: "stubs.vacationMode.name",
    descriptionKey: "stubs.vacationMode.description",
    sequencingReasonKey: "stubs.vacationMode.why",
  },
  {
    slug: "feedback",
    rId: "R11.5",
    name: "Send Feedback",
    description:
      "Your feedback helps shape what we build next for Pelican Energy TCI customers.",
    status: { kind: "live" },
    sequencingReason:
      "We want to hear what's working and what isn't while the app is still taking shape.",
    nameKey: "stubs.feedback.name",
    descriptionKey: "stubs.feedback.description",
    sequencingReasonKey: "stubs.feedback.why",
  },
  {
    slug: "meter-tools",
    rId: "R5.5+R5.6",
    name: "Meter Tools",
    description:
      "Submit a self-read of your meter when an automated read isn’t available, or see the energy mix powering your address.",
    status: { kind: "future" },
    sequencingReason:
      "These tools need a live link to your meter, and we're confirming how prepaid meters fit in.",
    nameKey: "stubs.meterTools.name",
    descriptionKey: "stubs.meterTools.description",
    sequencingReasonKey: "stubs.meterTools.why",
  },
];

// ---------------------------------------------------------------------------
// Bill view — 1 stub action
// ---------------------------------------------------------------------------

export const BILL_STUBS: StubAtom[] = [
  {
    slug: "share-bill",
    rId: "R3.4",
    name: "Share Bill",
    description:
      "Send a copy of this bill as proof of address — via email, WhatsApp, or printable PDF.",
    status: { kind: "future" },
    sequencingReason:
      "Sharing a bill as proof of address means generating a clean PDF and handing it to WhatsApp or email — that's what we're building.",
    // O-R1 (W9 re-CHECK): keyed even though no web route renders BILL_STUBS
    // today — an unkeyed atom leaks English the day a bill-stub detail route
    // appears. Covered by the KEYED_STUBS invariant so it cannot silently
    // regress.
    nameKey: "stubs.shareBill.name",
    descriptionKey: "stubs.shareBill.description",
    sequencingReasonKey: "stubs.shareBill.why",
  },
];

// ---------------------------------------------------------------------------
// Resolver — single authority for any "is this capability live?" question
// ---------------------------------------------------------------------------

const ALL_STUBS: StubAtom[] = [
  ...SERVICES_STUBS,
  ...REPORT_STUBS,
  ...SUPPORT_STUBS,
  ...MENU_STUBS,
  ...BILL_STUBS,
];

const SLUG_MAP = new Map<string, StubStatus>(
  ALL_STUBS.map((s) => [s.slug, s.status]),
);

export function featureStatus(slug: string): StubStatus {
  const status = SLUG_MAP.get(slug);
  if (!status) {
    throw new Error(`featureStatus: unknown slug "${slug}"`);
  }
  return status;
}
